import { useCallback } from 'react';
import styled, { css } from 'foundations-theming-specialty/utils';
import { ArrowDownIcon } from 'breadcrumbs-ui/icons';
import { useTranslation } from '../../hooks/useNavConfig';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { getIsExpanded, setExpandableMenuItemVisibility } from '../../reducers/navItems';
import useVerticalNavKeyboardControls from './hooks/useVerticalNavKeyboardControls';
import useVerticalNavFocus from './hooks/useVerticalNavFocus';
import { EXPANDABLE_NAV_GROUP_MAX_INITIAL_DISPLAY_ITEMS } from './constants';
import withTransientStyleProps from '../../utils/withTransientStyleProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledButton = styled.button.withConfig({
  displayName: "VerticalNavExpandableList__StyledButton",
  componentId: "sc-10udiwe-0"
})(({
  theme
}) => {
  return css(["display:flex;justify-content:flex-start;padding:12px 10px;color:", ";font-size:12px;font-weight:700;line-height:18px;-webkit-font-smoothing:auto;-moz-osx-font-smoothing:auto;background:transparent;width:100%;text-align:left;margin:0;border:0;align-items:center;&:hover{text-decoration:underline;}"], theme.color['global-shell-text-nav-item-hover']);
});
const StyledArrowDownIcon = styled(withTransientStyleProps(ArrowDownIcon))(({
  $isExpanded,
  theme
}) => {
  return css(["width:12px;fill:", ";margin-left:8px;transform:", ";"], theme.color['global-shell-icon-nav-item-hover'], $isExpanded ? 'rotate(180deg)' : 'initial');
});
const VerticalNavExpandableListButton = ({
  isExpanded,
  toggleOpen,
  id
}) => {
  const {
    t
  } = useTranslation();
  const label = isExpanded ? t('nav.menu.showLess') : t('nav.menu.showMore');
  const {
    ref
  } = useVerticalNavFocus({
    id
  });
  const {
    onKeyDown
  } = useVerticalNavKeyboardControls();
  return /*#__PURE__*/_jsxs(StyledButton, {
    ref: ref,
    onClick: toggleOpen,
    "aria-expanded": isExpanded,
    onKeyDown: onKeyDown({
      id,
      hasSubmenu: false,
      shouldShiftFocusOnOpen: false
    }),
    children: [label, /*#__PURE__*/_jsx(StyledArrowDownIcon, {
      $isExpanded: isExpanded
    })]
  });
};
const VerticalNavExpandableList = ({
  id,
  listItems
}) => {
  const dispatch = useAppDispatch();
  const isExpanded = useAppSelector(state => getIsExpanded(state, id));
  const triggerId = `${id}-trigger`;
  const toggleOpen = useCallback(() => {
    dispatch(setExpandableMenuItemVisibility({
      menuId: id,
      isExpanded: !isExpanded
    }));
  }, [dispatch, id, isExpanded]);
  if (listItems.length <= EXPANDABLE_NAV_GROUP_MAX_INITIAL_DISPLAY_ITEMS) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: listItems
    });
  }
  return /*#__PURE__*/_jsx("li", {
    role: "none",
    children: /*#__PURE__*/_jsxs("ul", {
      role: "group",
      children: [listItems.map((item, index) => {
        if (isExpanded || index < EXPANDABLE_NAV_GROUP_MAX_INITIAL_DISPLAY_ITEMS) {
          return item;
        }
        return null;
      }), /*#__PURE__*/_jsx(VerticalNavExpandableListButton, {
        toggleOpen: toggleOpen,
        isExpanded: !!isExpanded,
        id: triggerId
      })]
    })
  });
};
export default VerticalNavExpandableList;